
interface SupportedFormats {
    webp: boolean;
    avif: boolean;
    jpeg: boolean;
    png: boolean;
}

/**
 * Extended HTMLImageElement with fetchPriority support
 */
type ExtendedHTMLImageElement = HTMLImageElement & {
    fetchPriority?: 'high' | 'low' | 'auto';
};

interface ImageOptimizerOptions {
    alt?: string;
    className?: string;
    lazy?: boolean;
    fallback?: boolean;
    onLoad?: () => void;
    onError?: () => void;
    sizes?: string;
}

class ImageOptimizer {
    private static instance: ImageOptimizer;
    private cache: Map<string, HTMLImageElement> = new Map();
    private supportedFormats: SupportedFormats;

    private constructor() {
        this.supportedFormats = this.detectSupportedFormats();
    }

    public static getInstance(): ImageOptimizer {
        if (!ImageOptimizer.instance) {
            ImageOptimizer.instance = new ImageOptimizer();
        }
        return ImageOptimizer.instance;
    }

    private detectSupportedFormats(): SupportedFormats {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const formats: SupportedFormats = {
            webp: false,
            avif: false,
            jpeg: true,
            png: true
        };

        if (!ctx) return formats;

        try {
            formats.webp = canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0;
        } catch (e) {
            formats.webp = false;
        }

        if ('createImageBitmap' in window) {
            // Base64をUint8Arrayに直接変換（fetchを使わずにdata URLエラーを回避）
            const avifBase64 = 'AAAAIGZ0eXBhdmlmAAAAAGF2aWZtaWYxbWlhZk1BMUEAAADybWV0YQAAAAAAAAAoaGRscgAAAAAAAAAAcGljdAAAAAAAAAAAAAAAAGxpYmF2aWYAAAAADnBpdG0AAAAAAAEAAAAeaWxvYwAAAABEAAABAAEAAAABAAABGgAAABcAAAAoaWluZgAAAAAAAQAAABppbmZlAgAAAAABAABhdjAxQ29sb3IAAAAAamlwcnAAAABLaXBjbwAAABRpc3BlAAAAAAAAAAEAAAABAAAAEHBpeGkAAAAAAAwgICAAAAAxhdjFDgQAMAAAAABNjb2xybmNseAACAAIABoAAAAAXaXBtYQAAAAAAAAABAAEEAQKDBAAAAB9tZGF0EgAKCBgABogQEDQgMgkQAAAAB8dSLfI=';
            try {
                const binaryString = atob(avifBase64);
                const bytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                }
                const blob = new Blob([bytes], { type: 'image/avif' });
                createImageBitmap(blob)
                    .then(() => { formats.avif = true; })
                    .catch(() => { formats.avif = false; });
            } catch {
                formats.avif = false;
            }
        }
        return formats;
    }

    public getOptimalSource(basePath: string, extension: string = 'png'): string {
        const baseName = basePath.replace(/\.[^/.]+$/, '');
        if (this.supportedFormats.avif) {
            return `${baseName}.avif`;
        } else if (this.supportedFormats.webp) {
            return `${baseName}.webp`;
        } else {
            return `${baseName}.${extension}`;
        }
    }

    public createOptimizedImage(src: string, options: ImageOptimizerOptions = {}): HTMLImageElement | HTMLPictureElement {
        const { fallback = true } = options;
        if (fallback) {
            return this.createPictureElement(src, options);
        }
        const img = document.createElement('img');
        img.alt = options.alt || '';
        img.className = options.className || '';
        if (options.lazy !== false) {
            img.loading = 'lazy';
            img.decoding = 'async';
        }
        const optimizedSrc = this.getOptimalSource(src);
        if (options.onLoad) img.addEventListener('load', options.onLoad);
        if (options.onError) {
            img.addEventListener('error', () => {
                if (img.src !== src) {
                    img.src = src;
                } else if (options.onError) {
                    options.onError();
                }
            });
        }
        img.src = optimizedSrc;
        return img;
    }

    public createPictureElement(src: string, options: ImageOptimizerOptions = {}): HTMLPictureElement {
        const picture = document.createElement('picture');
        const baseName = src.replace(/\.[^/.]+$/, '');

        if (this.supportedFormats.avif) {
            const avifSource = document.createElement('source');
            avifSource.srcset = `${baseName}.avif`;
            avifSource.type = 'image/avif';
            if (options.sizes) avifSource.sizes = options.sizes;
            picture.appendChild(avifSource);
        }

        if (this.supportedFormats.webp) {
            const webpSource = document.createElement('source');
            webpSource.srcset = `${baseName}.webp`;
            webpSource.type = 'image/webp';
            if (options.sizes) webpSource.sizes = options.sizes;
            picture.appendChild(webpSource);
        }

        const img = document.createElement('img');
        img.src = src;
        img.alt = options.alt || '';
        img.className = options.className || '';
        if (options.lazy !== false) {
            img.loading = 'lazy';
            img.decoding = 'async';
        }
        if (options.onLoad) img.addEventListener('load', options.onLoad);
        if (options.onError) img.addEventListener('error', options.onError);
        picture.appendChild(img);
        return picture;
    }

    public async preloadImage(src: string, priority: 'high' | 'low' | 'auto' = 'low'): Promise<HTMLImageElement> {
        const optimizedSrc = this.getOptimalSource(src);
        if (this.cache.has(optimizedSrc)) {
            return Promise.resolve(this.cache.get(optimizedSrc)!);
        }

        return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => {
                this.cache.set(optimizedSrc, img);
                resolve(img);
            };
            img.onerror = () => {
                if (optimizedSrc !== src) {
                    const fallbackImg = new Image();
                    fallbackImg.onload = () => {
                        this.cache.set(src, fallbackImg);
                        resolve(fallbackImg);
                    };
                    fallbackImg.onerror = reject;
                    fallbackImg.src = src;
                } else {
                    reject(new Error(`Failed to load image: ${src}`));
                }
            };
            if ('fetchPriority' in img) {
                (img as ExtendedHTMLImageElement).fetchPriority = priority;
            }
            img.src = optimizedSrc;
        });
    }

    public async preloadImages(sources: string[], options: { concurrency?: number; priority?: 'high' | 'low' | 'auto' } = {}): Promise<unknown[]> {
        const { concurrency = 3, priority = 'low' } = options;
        const results: unknown[] = [];
        const queue = [...sources];

        while (queue.length > 0) {
            const batch = queue.splice(0, concurrency);
            const batchPromises = batch.map(src =>
                this.preloadImage(src, priority).catch(error => ({ error, src }))
            );
            const batchResults = await Promise.all(batchPromises);
            results.push(...batchResults);
        }
        return results;
    }

    public clearCache(): void {
        this.cache.clear();
    }
}

const imageOptimizer = ImageOptimizer.getInstance();
export default imageOptimizer;
